import { date, formatNumber, type Language } from '@eformulieren/engine'
import { DocumentVerwijzing, type SingleView } from '@eformulieren/model'

/**
 * Wat er van een waarde uit het dossier op het scherm komt (R-55, R-61), gedeeld door
 * `Verwijzing` en de cellen van `Subgrid`.
 *
 * **De vorm komt uit de waarde, en de opmaak uit de weergave als die er is.** Een verwijzing
 * krijgt de kale waarde uit het blokdocument (`bouwBlokdocument` in `packages/model`): een tekst,
 * een getal, een ja of nee, een lijst trefwoorden of keuzes, of een lijst bijlagen. Wat het veld in
 * de aanvraag was staat in de weergave van het bronveld (`FieldView`), die de store bij het
 * aanmaken bevriest uit de publicatie waaronder de aanvrager invulde (R-100). Met die weergave
 * staat een keuze er als haar label, een bedrag met euroteken en scheidingstekens, een datum en een
 * tijdstip uitgeschreven, en een getal met scheidingstekens; de labels in de taal van de lezer
 * (R-143). Een blok buiten dit product heeft geen weergave per bronveld; noemt zijn contract een
 * formaat, zoals een bedrag in euro, dan maakt de engine daar een weergave van (`referenceView`,
 * R-54). Zonder weergave (geen formaat, of een publicatie of instantie van vóór de bevriezing)
 * blijft het de kale waarde. Raden uit de waarde gebeurt nooit: een tekstveld met toevallig een
 * datum erin zou dan iets tonen wat de aanvrager niet schreef.
 *
 * **Het getal en de datum in dezelfde notatie als de rest van het product.** `getal` en `datum`
 * uit de engine, die ook de meldingen opmaken; zonder `Intl`, zodat Node en de browser hetzelfde
 * tonen (`taal.ts`). Het bedragveld zelf toont het euroteken buiten een invoerveld zonder
 * scheidingsteken (`Getal.tsx`); daar valt geen opmaak uit te hergebruiken, en een getal in een
 * invoerveld is ook iets anders dan een gegeven om te lezen.
 *
 * **Rich text is tekst, en blijft tekst.** In het blokdocument staat van een rich-text-veld alleen
 * de platte tekst, met een regeleinde tussen de blokken (`plainTextOf`); de opmaak zit in
 * `structuur` en die reist niet mee. Die platte tekst is niet ontsnapt: een getypte `<` staat er
 * kaal in, en in een revisie van vóór 26 oktober 2026 ook een opmaakmark als `<bold>` (`plainTextOf`
 * schreef die toen mee). Elke alinea gaat daarom als tekstknoop de pagina in,
 * nooit als HTML; React ontsnapt hem.
 *
 * **`undefined` betekent niets te tonen**, en dat is de enige plek waar dat wordt vastgesteld: de
 * aanroeper valt dan terug op de melding van R-61. Een lege tekst, een lege lijst, een object en
 * een lijst met een vorm die hier niet bij hoort (rijen, of een mengsel) zijn allemaal niets te
 * tonen, want `[object Object]` of een half getoonde lijst is erger dan een eerlijke melding.
 */
export type DossierView =
  | { readonly soort: 'tekst'; readonly alineas: readonly string[] }
  | { readonly soort: 'lijst'; readonly items: readonly string[] }
  | { readonly soort: 'bijlagen'; readonly items: readonly string[] }

export function dossierView(
  value: unknown,
  language: Language,
  view?: SingleView,
): DossierView | undefined {
  if (view !== undefined) {
    const formatted = formattedWith(value, language, view)
    if (formatted !== undefined) return formatted
  }
  if (typeof value === 'string') {
    const readable = machineMoment(value, language)
    if (readable !== undefined) return { soort: 'tekst', alineas: [readable] }
    const paragraphs = value.split('\n').filter((paragraph) => paragraph.trim() !== '')
    return paragraphs.length === 0 ? undefined : { soort: 'tekst', alineas: paragraphs }
  }
  if (typeof value === 'number') return Number.isFinite(value) ? { soort: 'tekst', alineas: [bareNumber(value, language)] } : undefined
  if (typeof value === 'boolean') return { soort: 'tekst', alineas: [yesNo(value, language)] }
  if (!Array.isArray(value) || value.length === 0) return undefined

  if (value.every((item) => typeof item === 'string' || (typeof item === 'number' && Number.isFinite(item)))) {
    return { soort: 'lijst', items: value.map((item) => String(item)) }
  }
  const attachments = value.map((item) => DocumentVerwijzing.safeParse(item))
  if (attachments.every((out) => out.success)) {
    return {
      soort: 'bijlagen',
      items: attachments.map(({ data }) => `${data.bestandsnaam} (${extent(data.grootteBytes, language)})`),
    }
  }
  return undefined
}

/**
 * Een moment of datum in de vorm van een machine (ISO 8601) als tekst voor een mens, zonder weergave
 * van het bronveld (ontwerp van het invulscherm, 29 oktober 2026).
 *
 * Een blok van buiten dit product (`call-publiceren`) heeft geen definitie in dit product en dus
 * geen weergave (`sourceView.ts` in het model): zijn sluitingsmoment kwam als
 * `2026-11-09T17:00:00+01:00` in beeld. Die vorm is ondubbelzinnig, dus dit is geen gok: alleen een
 * volledige datum, of een datum met kloktijd en eventueel seconden en zone, wordt herschreven, als
 * `9 november 2026, 17:00`. De kloktijd blijft die van de bron, zoals bij `timestamp`.
 */
function machineMoment(value: string, language: Language): string | undefined {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return date(value, language)
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?$/.test(value)) return timestamp(value, language)
  return undefined
}

/**
 * Een kaal getal zonder weergave: vanaf vijf cijfers met scheidingstekens (`750.000`), daaronder
 * zoals het staat.
 *
 * De grens is er omdat het soort getal niet bekend is: een jaartal (`2027`) hoort geen punt te
 * krijgen, een bedrag van een blok van buiten zonder weergave wel leesbaar te zijn. Een valutateken
 * staat er niet bij, want zonder weergave is niet bekend dat het een bedrag is; zie `amount` voor een
 * veld waarvan de weergave het zegt, ook een bedrag dat het contract als bedrag beschrijft (R-54).
 */
function bareNumber(value: number, language: Language): string {
  return Math.abs(value) >= 10_000 ? formatNumber(value, language) : String(value)
}

/** Ja of nee in de taal van de lezer, en niet `true` of `false`. */
export function yesNo(value: boolean, language: Language): string {
  if (language === 'nl') return value ? 'Ja' : 'Nee'
  return value ? 'Yes' : 'No'
}

/** De omvang van een bestand, zoals het bijlageveld hem toont. */
export function extent(bytes: number, language: Language): string {
  // Een megabyte is een miljoen bytes, zoals de engine dat ook aanhoudt.
  const mb = bytes / 1_000_000
  if (mb >= 0.1) return `${formatNumber(Math.round(mb * 10) / 10, language)} MB`
  return `${formatNumber(Math.max(1, Math.round(bytes / 1000)), language)} kB`
}

/**
 * De waarde in de vorm die de weergave voorschrijft, of undefined als de soort hier niets aan
 * verandert of de waarde niet bij de soort past. Dan geldt de vorm uit de waarde zelf, zodat er
 * nooit iets verdwijnt: een keuzecode die de publicatie niet kent blijft als code staan.
 */
function formattedWith(value: unknown, language: Language, view: SingleView): DossierView | undefined {
  switch (view.soort) {
    case 'keuze': {
      const labelOf = (code: string | number): string =>
        view.opties.find((option) => option.waarde === String(code))?.label[language] ?? String(code)
      if (typeof value === 'string' && value !== '') return { soort: 'tekst', alineas: [labelOf(value)] }
      if (Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === 'string' || typeof item === 'number')) {
        return { soort: 'lijst', items: value.map(labelOf) }
      }
      return undefined
    }
    case 'bedrag':
      return typeof value === 'number' && Number.isFinite(value)
        ? { soort: 'tekst', alineas: [amount(value, language)] }
        : undefined
    case 'getal':
      return typeof value === 'number' && Number.isFinite(value)
        ? { soort: 'tekst', alineas: [formatNumber(value, language)] }
        : undefined
    case 'datum':
      return typeof value === 'string' && value !== '' ? { soort: 'tekst', alineas: [date(value, language)] } : undefined
    case 'tijdstip':
      return typeof value === 'string' && value !== '' ? { soort: 'tekst', alineas: [timestamp(value, language)] } : undefined
    case 'tekst':
      return undefined
  }
}

/**
 * Een bedrag met euroteken en scheidingstekens: `€ 650.000` en `€ 650,000`. Het teken vooraan in
 * beide talen, zoals het bedragveld het in de aanvraag toont. Eén valuta, want het bedragveld kent
 * er één (`AmountView` in het model).
 */
export function amount(value: number, language: Language): string {
  return `€ ${formatNumber(value, language)}`
}

/**
 * Een tijdstip zoals het datumveld het opslaat (`2026-11-02T14:30`, lokale kloktijd zonder zone,
 * zie `Datum.tsx`) als `2 november 2026, 14:30`. Geen omrekening naar een zone: het antwoord is
 * een kloktijd. Een andere vorm gaat ongewijzigd door, net als bij `datum`.
 */
export function timestamp(value: string, language: Language): string {
  const parts = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value)
  if (parts === null) return value
  const [, day, clock] = parts
  if (day === undefined || clock === undefined) return value
  return `${date(day, language)}, ${clock}`
}

/**
 * Een leesbare naam voor een bouwblok waarvan de pod de weergavenaam niet kent (audit van 30
 * oktober 2026, R-58).
 *
 * De pod kent alleen de weergavenaam van zijn eigen blok (`bloknaam`, ontwerp 9.5); voor een blok
 * van buiten heeft hij alleen de slug van het contract. Die stond letterlijk in beeld, "uit
 * call-publiceren". Hier wordt hij een woordgroep: streepjes worden spaties, "uit call publiceren",
 * in kleine letters omdat hij midden in een zin staat. Dat is een terugval en geen naam; de echte naam hoort de
 * configurator bij het bouwen mee te geven, zoals hij dat voor het eigen blok al doet.
 */
export function readableBlockName(slug: string): string {
  const words = slug.replace(/[-_]+/g, ' ').trim()
  return words === '' ? slug : words
}
